import { Room as OfficialTwilioRoom } from 'twilio-video';

import { logger } from '../../../datadog/logger';
import { noop } from '../../../utils';
import { RoomDelegator } from '../../core/room/RoomDelegator';
import { LocalParticipant } from '../../core/room/participant/LocalParticipant';
import { RoomEvent, RoomEventHandlerMap } from '../../core/room/types';
import { TwilioLocalParticipant } from './participant/LocalParticipant';
import { TwilioRemoteParticipant } from './participant/RemoteParticipant';
import { createTwilioRoomEventHandler, getTwilioRoomEvent, isEventSupported } from './utils';

export class TwilioRoom implements RoomDelegator {
  #room: OfficialTwilioRoom;
  #localParticipant: LocalParticipant;

  constructor(room: OfficialTwilioRoom) {
    this.#room = room;
    this.#localParticipant = new TwilioLocalParticipant(room.localParticipant);
  }

  get name() {
    return this.#room.name;
  }

  get sid() {
    return this.#room.sid;
  }

  get localParticipant() {
    return this.#localParticipant;
  }

  get remoteParticipants() {
    return new Map(
      Array.from(this.#room.participants, ([sid, participant]) => [sid, new TwilioRemoteParticipant(participant)]),
    );
  }

  async disconnect(): Promise<void> {
    this.#room.disconnect();
  }

  on<K extends RoomEvent>(event: K, handler: RoomEventHandlerMap[K]): () => void {
    if (!isEventSupported(event as string)) {
      logger.warn(`[TwilioRoom: on] Unsupported event type: ${event}`);
      return noop;
    }

    const twilioEvent = getTwilioRoomEvent(event);
    if (!twilioEvent) {
      logger.warn(`[TwilioRoom: on] No Twilio mapping for event: ${event}`);
      return noop;
    }

    const twilioHandler = createTwilioRoomEventHandler(event, handler);

    // requires type assertions because Twilio Room's `on()` function lacks a generic or catch-all overload
    this.#room.on(
      twilioEvent as Parameters<OfficialTwilioRoom['on']>[0],
      twilioHandler as unknown as Parameters<OfficialTwilioRoom['on']>[1],
    );

    return () => {
      this.#room.off(twilioEvent, twilioHandler);
    };
  }

  // Twilio specific instance methods, use with type assertion in select situations for backwards compatibility

  async getStats() {
    return this.#room.getStats();
  }

  getTwilioLocalParticipant() {
    return this.#room.localParticipant;
  }
}
